import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import type { IUser } from '@/types/User';
import { LocalStorage } from 'quasar';

export const useAuthStore = defineStore(
  'auth',
  () => {
    const token = ref<string | null>(null);
    const is_agent = ref<number | null>(0);
    const reg_email = ref<string | null>('');
    const reg_number = ref<string | number | null>('');
    const user = ref<IUser>({});
    const keepLoggedIn = ref(false);
    const id = ref(0);
    const is_user_verified = ref(false);
    const level_name = ref('');
    const level_icon = ref('');
    const slug = ref<string>('');
    const is_have_subscription = ref(0);
    const review_total = ref<string>('');
    const review_likes_total = ref<string>('');
    const review_views_total = ref<string>('');
    const country = ref<string>('');
    const has_bank_account = ref(false);

    const setCountry = (valueOfCountry: string) => {
      country.value = valueOfCountry;
    };

    const getCountry = (): string => {
      return country.value;
    };
    const setHasBankAccount = (valueOfdata: boolean) => {
      has_bank_account.value = valueOfdata;
    };

    const getHasBankAccount = (): boolean => {
      return has_bank_account.value;
    };

    const setToken = (newToken: string) => {
      token.value = newToken;
    };

    const setRegEmail = (new_reg_email: string) => {
      reg_email.value = new_reg_email;
    };

    const setRegNumber = (new_reg_number: string | number) => {
      reg_number.value = new_reg_number;
    };

    const setIsAgent = (is_agent_val: number) => {
      is_agent.value = is_agent_val;
    };

    const setIsHaveSubscription = (is_have_subscription_val: boolean) => {
      is_have_subscription.value = is_have_subscription_val ? 1 : 0;
    };

    const setUser = (newUser: IUser) => {
      user.value = newUser;
    };

    const setUserName = (name: string) => {
      const nameArr = name.split(' ');
      user.value.first_name = nameArr[0];
      user.value.last_name = nameArr[1];
    };

    const setKeepLoggedIn = (keep: boolean) => {
      keepLoggedIn.value = keep;
    };

    const getToken = (): string | null => {
      return token.value;
    };

    const getRegEmail = (): string | null => {
      return reg_email.value;
    };

    const getRegNumber = (): string | number | null => {
      return reg_number.value;
    };

    const getIsAgent = () => {
      return is_agent.value;
    };

    const getIsHaveSubscription = () => {
      return is_have_subscription.value;
    };

    const isLoggedIn = computed(() => !!token.value);

    const logout = () => {
      is_agent.value = 0;
      token.value = null;
      user.value = {};
      keepLoggedIn.value = false;
      is_user_verified.value = false;
      level_icon.value = '';
      level_name.value = '';

      review_total.value = '';
      review_likes_total.value = '';
      review_views_total.value = '';
      has_bank_account.value = false;
    };

    const keepMeLoggedIn = () => {
      return keepLoggedIn.value;
    };

    const setId = (newId: number) => {
      id.value = newId;
    };

    const setIsUserVerified = (verified: boolean) => {
      is_user_verified.value = verified;
    };

    const setSlug = (newSlug: string) => {
      user.value.slug = newSlug;
    };

    const setLevelName = (name: string) => {
      level_name.value = name;
    };

    const setLevelIcon = (icon: string) => {
      level_icon.value = icon;
    };

    const setReviewTotal = (total: string) => {
      review_total.value = total;
    };

    const setReviewViewsTotal = (views_total: string) => {
      review_views_total.value = views_total;
    };

    const setReviewLikesTotal = (likes_total: string) => {
      review_likes_total.value = likes_total;
    };

    const getReviewTotal = (): string => {
      return review_total.value;
    };

    const getReviewViewsTotal = (): string => {
      return review_views_total.value;
    };

    const getReviewLikesTotal = (): string => {
      return review_likes_total.value;
    };
    ;
    const getId = (): number => {
      return id.value;
    };

    const getIsUserVerified = (): boolean => {
      return is_user_verified.value;
    };

    const getLevelName = (): string => {
      return level_name.value;
    };

    const getLevelIcon = (): string => {
      return level_icon.value;
    };
    // Inside your store definition, add this before the return statement:
    const handleAuthMessage = (event: MessageEvent) => {
      if (event.data.type !== 'AKWAD_AUTH' || event.data.store !== 'auth') return;
      switch (event.data.action) {
        case 'LOGIN':
          const payload = event.data.payload;
          setToken(payload.token);
          setUser(payload.user);
          setIsAgent(payload.is_agent);
          setId(payload.id);
          setIsUserVerified(payload.is_user_verified);
          setLevelName(payload.level_name);
          setLevelIcon(payload.level_icon);
          setReviewTotal(payload.review_total);
          setReviewLikesTotal(payload.review_likes_total);
          setReviewViewsTotal(payload.review_views_total);
          setCountry(payload.country);
          setHasBankAccount(payload.has_bank_account);
          if (payload.slug) setSlug(payload.slug);
          break;
        case 'LOGOUT':
          logout();
          break;
      }
    };

    return {
      token,
      user,
      keepLoggedIn,
      setToken,
      setUser,
      setKeepLoggedIn,
      setUserName,
      isLoggedIn,
      logout,
      keepMeLoggedIn,
      getToken,
      is_agent,
      setIsAgent,
      getIsAgent,
      reg_email,
      reg_number,
      setRegNumber,
      getRegNumber,
      setRegEmail,
      getRegEmail,
      is_have_subscription,
      setIsHaveSubscription,
      getIsHaveSubscription,
      id,
      is_user_verified,
      level_name,
      level_icon,
      setId,
      setIsUserVerified,
      setSlug,
      setLevelName,
      setLevelIcon,
      getId,
      getIsUserVerified,
      getLevelName,
      getLevelIcon,
      handleAuthMessage,
      getReviewTotal,
      getReviewViewsTotal,
      getReviewLikesTotal,
      setReviewTotal,
      setReviewViewsTotal,
      setReviewLikesTotal,
      review_total,
      review_likes_total,
      review_views_total,
      country,
      setCountry,
      getCountry,
      setHasBankAccount,
      getHasBankAccount,
      has_bank_account,
    };
  },
  {
    // persist:
    //   process.env.SERVER === 'false'
    //     ? {
    //         key: 'auth',
    //         storage: localStorage,
    //         paths: ['token', 'user', 'keepLoggedIn', 'is_agent'],
    //       }
    //     : false,
    persist: {
      key: 'auth',
      storage: LocalStorage,
      paths: [
        'token',
        'user',
        'keepLoggedIn',
        'is_agent',
        'id',
        'is_user_verified',
        'is_have_subscription',
        'review_total',
        'review_likes_total',
        'review_views_total',
        'country',
        'has_bank_account',
      ],
    },
  },
);
